<script lang="ts">
import { defineComponent, PropType, ref, onMounted } from 'vue'
import CUSTOM_FIELD_TYPES from '@/constants/customFieldTypes'
import TextareaForm from '@/components/form/TextareaForm.vue'
import SelectorForm from '@/components/form/SelectorForm.vue'
import CheckboxForm from '@/components/form/CheckboxForm.vue'
import CheckboxItem from '@/components/form/CheckboxItem.vue'
import MultiSelectorForm from '@/components/form/MultiSelectorForm.vue'
import htmlUtil from '@/utilities/html'

export default defineComponent({
  name: 'InputCustomFieldItem',

  components: {
    TextareaForm,
    SelectorForm,
    CheckboxForm,
    CheckboxItem,
    MultiSelectorForm,
  },

  props: {
    /**
     * カスタムフィールドアイテムの ID 番号
     */
    fieldId: {
      type: Number,
      required: true,
    },

    /**
     * 見出し文言
     */
    header: {
      type: String,
      default: '',
    },

    /**
     * サーバ側（admin 画面のカスタムフィールド参照）で設定されるカスタムフィールドの種別
     * @description 'checkbox' は、現状ほとんど使われていない（過去に数件だけある）ため、タイプとしては受けるが、レンダリングされる UI としては、通常のチェックボックスと同じものとする
     */
    fieldType: {
      type: String as PropType<CustomFieldType>,
      required: true,
    },

    namespace: {
      type: String,
      default: '',
    },

    name: {
      type: String,
      default: '',
    },

    modelValue: {
      type: [String, Number, Boolean, Array] as PropType<string | number | boolean | string[] | number[] | boolean[] | undefined>,
      required: true,
    },

    /**
     * hidden で送る、カスタムフィールドアイテムの id 情報の name 属性値
     * @description 以下の形で、1 つのアイテムにつき、2 つの情報が必要
     *              - id 情報の hidden
     *                <input
     *                  type="hidden"
     *                  :name="contact[contact_custom_fields_attributes][#{index}][custom_field_id]"
     *                  :value="カスタムフィールドアイテムの id">
     *
     *              - 入力値
     *                <input
     *                  type="●●●●"
     *                  :name="contact[contact_custom_fields_attributes][#{index}][value]"
     *                  :value="カスタムフィールドアイテムの入力値">
     */
    idAttrName: {
      type: String,
      required: true,
    },

    selectItems: {
      type: Array as PropType<SelectItem[]>,
      default: () => [],
    },

    validationScheme: {
      type: Object as PropType<ValidationScheme>,
      default: () => ({}),
    },
  },

  emits: ['update:modelValue'],

  setup(props) {
    const headerWithLink = ref('')

    onMounted(() => {
      headerWithLink.value = htmlUtil.toAnchorFromUrlText(props.header, '<i class="i-window__blank"></i>', ['a'])
    })

    return {
      // constants
      CUSTOM_FIELD_TYPES,

      // data
      headerWithLink,
    }
  }
})
</script>

<template>
  <div class="div">
    <!-- テキストエリア -->
    <TextareaForm
      v-if="fieldType === CUSTOM_FIELD_TYPES.TEXT"
      :header="headerWithLink"
      :namespace="namespace"
      :name="name"
      :rows="2"
      :model-value="(modelValue as string)"
      :validation-scheme="validationScheme"
      @update:model-value="$emit('update:modelValue', $event)" />

    <!-- ドロップダウンリスト -->
    <SelectorForm
      v-else-if="fieldType === CUSTOM_FIELD_TYPES.SELECT_SINGLE"
      :header="headerWithLink"
      :namespace="namespace"
      :name="name"
      :model-value="(modelValue as SingleSelect)"
      :items="selectItems"
      default-selector-label="選択してください"
      default-selector-value=""
      :validation-scheme="validationScheme"
      @update:model-value="$emit('update:modelValue', $event)" />
    <!-- <RadioButtonForm
      :header="header"
      :namespace="namespace"
      :name="name"
      :model-value="(modelValue as SingleSelect)"
      :items="selectItems"
      :validation-scheme="validationScheme"
      @update:model-value="$emit('update:modelValue', $event)" /> -->

    <!-- マルチセレクター -->
    <MultiSelectorForm
      v-else-if="fieldType === CUSTOM_FIELD_TYPES.SELECT_MULTI"
      :header="headerWithLink"
      :namespace="namespace"
      :name="name"
      :model-value="(modelValue as MultiSelect)"
      :items="selectItems"
      :validation-scheme="validationScheme"
      @update:model-value="$emit('update:modelValue', $event)" />

    <!-- チェックボックス -->
    <CheckboxForm
      v-else-if="fieldType === CUSTOM_FIELD_TYPES.CHECKBOX_MULTI"
      :header="headerWithLink"
      :namespace="namespace"
      :name="name"
      :model-value="(modelValue as MultiSelect)"
      :items="selectItems"
      :cols="2"
      :validation-scheme="validationScheme"
      @update:model-value="$emit('update:modelValue', $event)" />

    <!-- チェックボックス -->
    <CheckboxItem
      v-else-if="fieldType === CUSTOM_FIELD_TYPES.CHECKBOX"
      :namespace="namespace"
      :model-value="(modelValue as number)"
      :name="name"
      :value="1"
      :false-value="0"
      :label="headerWithLink"
      :validation-scheme="validationScheme"
      @update:model-value="$emit('update:modelValue', $event)" />

    <input
      class="input"
      type="hidden"
      :name="idAttrName"
      :value="fieldId">
  </div>
</template>
